<div class="mx-auto max-w-3xl px-6 py-8">

  <!-- Back link -->
  <a routerLink="/cloud" class="mb-4 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
    <lucide-icon name="chevron-left" [size]="14" [strokeWidth]="2"></lucide-icon>
    Monitoring
  </a>

  <!-- Header -->
  <div class="mb-6">
    <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Finding</p>

    <!-- Finding metadata (loads independently) -->
    <ng-container *ngIf="!findingLoading; else headerSkeleton">
      <div *ngIf="finding; else findingUnknown">
        <h1 class="mt-1 text-lg font-semibold text-slate-900">{{ finding.title }}</h1>
        <p class="mt-1 text-sm text-slate-600">{{ finding.summary }}</p>
        <div class="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span class="inline-flex items-center rounded-md px-2 py-1 font-semibold" [ngClass]="severityBadgeClass(finding.severity)">{{ finding.severity }}</span>
          <span class="inline-flex items-center rounded-md px-2 py-1 font-medium"
            [class.bg-blue-100]="finding.findingState === 'OPEN'"
            [class.text-blue-700]="finding.findingState === 'OPEN'"
            [class.bg-slate-100]="finding.findingState !== 'OPEN'"
            [class.text-slate-500]="finding.findingState !== 'OPEN'"
          >{{ finding.findingState === 'OPEN' ? 'Open' : 'Resolved' }}</span>
          <span class="rounded-md bg-slate-100 px-2 py-1 font-medium text-slate-600">{{ serviceTypeLabel(finding.serviceType) }}</span>
          <span *ngIf="finding.region" class="rounded-md bg-slate-100 px-2 py-1 font-medium text-slate-500">{{ finding.region }}</span>
        </div>
        <dl class="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div class="col-span-2">
            <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Resource</dt>
            <dd class="mt-0.5 break-all font-mono text-xs text-slate-700">{{ finding.resourceId }}</dd>
          </div>
          <div>
            <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Observed</dt>
            <dd class="mt-0.5 text-slate-600">{{ finding.observedAt | date:'medium' }}</dd>
          </div>
          <div *ngIf="finding.resolvedAt">
            <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Resolved</dt>
            <dd class="mt-0.5 text-slate-600">{{ finding.resolvedAt | date:'medium' }}</dd>
          </div>
        </dl>
      </div>

      <!-- Finding not in the current list (e.g. aged out) — still show its id + diagnosis -->
      <ng-template #findingUnknown>
        <h1 class="mt-1 text-lg font-semibold text-slate-900">Finding detail</h1>
        <p class="mt-1 break-all font-mono text-xs text-slate-500">{{ findingId }}</p>
        <p class="mt-2 text-sm text-slate-500">This finding isn't in the current findings list, but its diagnosis is shown below if available.</p>
      </ng-template>
    </ng-container>

    <ng-template #headerSkeleton>
      <div class="mt-2 h-6 w-2/3 animate-pulse rounded bg-slate-100"></div>
      <div class="mt-2 h-4 w-1/2 animate-pulse rounded bg-slate-100"></div>
    </ng-template>
  </div>

  <!-- Diagnosis (reused shared component) -->
  <app-diagnosis-section [view]="enrichment" [loading]="enrichmentLoading"></app-diagnosis-section>

  <!-- Evidence provenance (from the persisted bundle) -->
  <div *ngIf="evidence" class="mb-6">
    <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Evidence</p>
    <div class="rounded-lg border border-slate-200 bg-white px-4 py-4 text-sm">
      <dl class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Source</dt>
          <dd class="mt-0.5 text-slate-700">{{ evidence.logSource || 'metrics only' }}</dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Error lines</dt>
          <dd class="mt-0.5 text-slate-700">{{ evidence.totalErrorLines }}</dd>
        </div>
        <div *ngIf="windowMinutes !== null">
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Search window</dt>
          <dd class="mt-0.5 text-slate-700">{{ windowMinutes }} min</dd>
        </div>
        <div>
          <dt class="text-xs font-medium uppercase tracking-wide text-slate-400">Collected</dt>
          <dd class="mt-0.5 text-slate-700">{{ evidence.collectedAt | date:'short' }}</dd>
        </div>
      </dl>

      <!-- Ranked signatures beyond the top one shown in the diagnosis -->
      <div *ngIf="evidence.topSignatures.length" class="mt-4">
        <p class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Top signatures</p>
        <ul class="space-y-1">
          <li *ngFor="let s of evidence.topSignatures" class="flex items-start gap-2 text-xs">
            <span class="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{{ s.count }}×</span>
            <span class="min-w-0 flex-1 truncate font-mono text-slate-600" [title]="s.sample">{{ s.template }}</span>
          </li>
        </ul>
      </div>

      <p class="mt-3 text-[11px] text-slate-400">
        {{ evidence.queries.length }} quer{{ evidence.queries.length === 1 ? 'y' : 'ies' }} run
        <span *ngIf="evidence.redactionApplied"> · redacted before storage</span>
      </p>
    </div>
  </div>

</div>
